import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout, { LegalList, LegalSection } from '../../components/legal/LegalPageLayout';
import {
  ANALYTICS_EFFECTIVE_DATE,
  COMPANY,
  LEGAL_LAST_UPDATED,
  SUPPORT_EMAIL,
} from '../../constants/legal';

// 記載内容は実装に合わせて確認済み。
//   - 認証情報はブラウザの localStorage に保存（src/context/AuthContext.jsx:55-57、有効期限7日）
//   - アクセス解析は Google アナリティクス（GA4、public/index.html の gtag.js、#263）。広告配信用の Cookie は未導入
//   - 決済代行の provider 既定値は 'univapay'（backend/modules/db/models.py:229）
//
// ⚠️ 要確認（確定したら本文を更新する）
//   1. 保存期間（第7条）は既定案。法務確認のうえ確定させる
//   2. 決済代行会社の**正式な法人名**。'univapay' はサービス名であって法人名の裏付けにならないため、
//      第4条では社名を出さず「当社が委託する決済代行会社」と表記している（利用規約 第4条と同じ扱い）。
//      加盟店契約で正式名称を確認できたら差し替える
//   3. 第4条のカード情報の取扱いは #35（Univapay 統合）の実装確定後に実態と一致するか再確認する。
//      サーバー側でカード情報を受ける実装になった場合、本条は事実と異なるものになる

function PrivacyPage() {
  return (
    <LegalPageLayout title="プライバシーポリシー" lastUpdated={LEGAL_LAST_UPDATED}>
      <Typography sx={{ fontSize: 14, lineHeight: 1.9, color: 'rgba(216, 236, 250, 0.86)' }}>
        {COMPANY.name}（以下「当社」といいます）は、「{COMPANY.serviceName}
        」（以下「本サービス」といいます）における利用者の個人情報の取扱いについて、以下のとおり定めます。
      </Typography>

      <LegalSection heading="第1条（事業者情報）">
        <LegalList
          ordered={false}
          items={[
            `事業者名：${COMPANY.name}`,
            `所在地：${COMPANY.address}`,
            `代表者名：${COMPANY.representative}`,
            `個人情報に関するお問い合わせ先：${SUPPORT_EMAIL}`,
          ]}
        />
      </LegalSection>

      <LegalSection heading="第2条（取得する情報）">
        <Typography sx={{ mb: 1 }}>当社は、本サービスの提供にあたり次の情報を取得します。</Typography>
        <LegalList
          items={[
            'メールアドレス（アカウント登録時にご入力いただくもの）',
            'パスワード（当社は平文では保持せず、ハッシュ化した値のみを保存します）',
            'ご契約内容に関する情報（契約コンテンツ、契約状況、契約期間、請求金額、決済結果）',
            'アクセスログ（IPアドレス、ブラウザの種類、アクセス日時、閲覧したページ）',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第3条（利用目的）">
        <Typography sx={{ mb: 1 }}>当社は、取得した情報を次の目的で利用します。</Typography>
        <LegalList
          items={[
            '本サービスの提供、本人認証、および閲覧権限の管理',
            '料金の請求および決済に関する処理',
            'お問い合わせへの対応、および必要なご連絡',
            '不正アクセス、規約違反その他の不正利用の検知および防止',
            '本サービスの障害対応、品質改善および新機能の検討',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第4条（クレジットカード情報の取扱い）">
        <LegalList
          items={[
            '本サービスの決済は、当社が委託する決済代行会社の提供する決済サービスを通じて行われます。',
            'クレジットカード番号、有効期限およびセキュリティコードは、決済代行会社において処理されます。当社はこれらを保持しません。',
            '当社が保持するのは、決済結果および決済を識別するための番号に限られます。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第5条（第三者提供）">
        <Typography sx={{ mb: 1 }}>
          当社は、次の場合を除き、利用者の個人情報を第三者に提供しません。
        </Typography>
        <LegalList
          items={[
            '利用者ご本人の同意がある場合',
            '料金の決済に必要な範囲で、前条の決済代行会社に提供する場合',
            '法令に基づき開示が求められた場合、または裁判所・捜査機関等から適法な要請を受けた場合',
            '人の生命、身体または財産の保護のために必要があり、ご本人の同意を得ることが困難な場合',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第6条（Cookie および外部送信）">
        <LegalList
          items={[
            '当社は、ログイン状態を保持するために、認証用のトークンを利用者のブラウザ内（ローカルストレージ）に保存します。当該トークンには有効期限が設定されています。',
            '当社は、本サービスの利用状況を把握し、サービスの改善に役立てるため、Google LLC が提供するアクセス解析ツール「Google アナリティクス」を利用しています。',
            'Google アナリティクスは Cookie 等を用いて、閲覧したページの URL、参照元、閲覧日時、利用端末・ブラウザの種類、おおよその地域等の情報を Google LLC に送信します。これらの情報は氏名・メールアドレス等の個人を特定する情報を含まず、当社は利用状況の把握とサービスの改善の目的にのみ利用します。',
            'Google による情報の取扱いは、Google のプライバシーポリシー（https://policies.google.com/privacy）および「Google のサービスを使用するサイトやアプリから収集した情報の Google による使用」（https://policies.google.com/technologies/partner-sites）をご確認ください。',
            '利用者は、ブラウザの設定で Cookie を無効にするか、Google アナリティクス オプトアウト アドオン（https://tools.google.com/dlpage/gaoptout）を利用することで、Google アナリティクスによる情報の送信を停止できます。',
            '当社は、本サービスにおいて広告配信を目的とした Cookie を使用していません。'
          ]}
        />
      </LegalSection>

      <LegalSection heading="第7条（保存期間）">
        <LegalList
          items={[
            'アカウントに関する情報は、退会後1年を経過した時点で削除または復元できない形に加工します。',
            '料金の請求および決済に関する記録は、法令上保存が求められる期間（原則7年間）保存します。',
            'アクセスログは、不正利用の調査および障害対応に必要な期間保存します。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第8条（安全管理措置）">
        <LegalList
          items={[
            'パスワードはハッシュ化して保存し、通信は暗号化（HTTPS）します。',
            '個人情報を取り扱う権限は、業務上必要な範囲に限定します。',
            '不正アクセスその他の事故が判明した場合は、速やかに調査し、必要な範囲で利用者へご連絡します。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第9条（開示・訂正・削除等の請求）">
        <LegalList
          items={[
            `利用者は、当社が保有するご自身の個人情報について、開示、訂正、利用停止または削除を請求することができます。請求は ${SUPPORT_EMAIL} までメールでご連絡ください。`,
            'ご本人であることを確認できた後、法令に従い遅滞なく対応いたします。',
            '法令により当社が保存を義務付けられている情報については、削除にお応えできない場合があります。',
          ]}
        />
      </LegalSection>

      <LegalSection heading="第10条（本ポリシーの改定）">
        <Typography>
          当社は、法令の変更または本サービスの内容の変更に応じて本ポリシーを改定することがあります。改定する場合は、変更後の内容および効力発生日を本サービス上に表示します。
        </Typography>
      </LegalSection>

      <LegalSection heading="附則">
        <Typography>
          {`第6条の改定（Google アナリティクスの利用）は、${ANALYTICS_EFFECTIVE_DATE}から効力を生じます。`}
        </Typography>
      </LegalSection>

      <LegalSection heading="お問い合わせ">
        <Typography>
          本ポリシーに関するお問い合わせは、{SUPPORT_EMAIL} までメールでご連絡ください。
        </Typography>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default PrivacyPage;
